import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { OrganCategory } from "../../redux/apiPath/api"; // Fix this import

export const createOrganCategory = createAsyncThunk(
  "organCategory/createOrganCategory",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await OrganCategory.createOrganCategory(formData);
      return response.data;
    } catch (error) {
      // Extract complete error message
      const message =
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to create organ category";

      // If there are validation errors, include them
      const validationErrors = error.response?.data?.errors || {};
      const errorDetails = Object.values(validationErrors).join(", ");

      const fullMessage = errorDetails
        ? `${message}. ${errorDetails}`
        : message;

      return rejectWithValue(fullMessage);
    }
  }
);

export const updateOrganCategory = createAsyncThunk(
  "organCategory/updateOrganCategory",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await OrganCategory.updateOrganCategory(id, data);
      return response.data;
    } catch (error) {
      // Extract complete error message
      const message =
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to update organ category";

      // If there are validation errors, include them
      const validationErrors = error.response?.data?.errors || {};
      const errorDetails = Object.values(validationErrors).join(", ");

      const fullMessage = errorDetails
        ? `${message}. ${errorDetails}`
        : message;

      return rejectWithValue(fullMessage);
    }
  }
);

export const getAllOrganCategories = createAsyncThunk(
  "organCategory/getAllOrganCategories",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await OrganCategory.getAllOrganCategories(params);
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to fetch organ categories";
      return rejectWithValue(message);
    }
  }
);

export const deleteOrganCategory = createAsyncThunk(
  "organCategory/deleteOrganCategory",
  async (id, { rejectWithValue }) => {
    try {
      const response = await OrganCategory.deleteOrganCategory(id);
      return response.data.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to delete organ category";
      return rejectWithValue(message);
    }
  }
);

export const undoDeleteOrganCategory = createAsyncThunk(
  "organCategory/undoDeleteOrganCategory",
  async (id, { rejectWithValue }) => {
    try {
      const response = await OrganCategory.undoDeleteOrganCategory(id);
      return response.data.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to restore organ category";
      return rejectWithValue(message);
    }
  }
);

const organCategorySlice = createSlice({
  name: "organCategory",
  initialState: {
    loading: false,
    error: null,
    pagination: { total: 0, page: 1, limit: 10, totalPages: 1 },
    statusCounts: { all: 0, active: 0, inactive: 0, deleted: 0 },
    search: "",
    status: "all",
    limit: 10,
    page: 1,
    success: false,
    organCategory: [],
  },

  reducers: {
    resetOrganCategoryState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
      state.organCategory = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createOrganCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createOrganCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.organCategory = action.payload;
      })
      .addCase(createOrganCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })
      .addCase(getAllOrganCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllOrganCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.organCategory = action.payload.data;
        state.pagination = action.payload.pagination;
        state.statusCounts = action.payload.statusCounts;
      })
      .addCase(getAllOrganCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteOrganCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteOrganCategory.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(deleteOrganCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(undoDeleteOrganCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(undoDeleteOrganCategory.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(undoDeleteOrganCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateOrganCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateOrganCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.updatedField = action.payload;
      })
      .addCase(updateOrganCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      });
  },
});

export const { resetOrganCategoryState } = organCategorySlice.actions;
export default organCategorySlice.reducer;
